import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Box, CircularProgress, Typography, styled } from "@mui/material";
import { useEffect } from "react";
import { pricePrediction } from "../Services/apiRoutes";
import { Icon } from "../components/Icon";
import { MarketRate } from "../components/MarketRate";
import { getCryptoLogoMap } from "../utils/utility";
import ToolTip from "../components/ToolTip";
import QuestionMarkSvg from '../components/QuestionMarkSvg'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#D5B458",
        color: theme.palette.common.black,
        padding: "10px 20px",
        borderRight: "1px solid #FFFFFF",
        fontFamily: "Inter",
        marginTop: "2px",
        fontWeight: "700",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function formatReturns(number) {
    if (!isNaN(number)) {
        if (Math.abs(number) < 1) { return Number(number)?.toFixed(2); }
        else { return Number(number)?.toFixed(0); }
    } else {
        return number;
    }
}
function formatPredictionAccuracy(val) {
    if (val < 0) {
        return ((5 - (Math.ceil(Math.abs(val) / 25))) * 5).toString();
    } else if (val === 0) {
        return "5";
    } else {
        return val.toFixed(0);
    }
}

function createData(index, coin, pr, pa, day, month, year) {
    coin = coin.split("-")[0];
    let predictionPrice = Number(pr)?.toFixed(3);
    let predictionAccuracy = formatPredictionAccuracy(pa * 100);
    let d = formatReturns(day);
    let m = formatReturns(month);
    let y = formatReturns(year);
    // console.log({ index, coin, predictionPrice, predictionAccuracy, d, m, y });
    return { index, coin, predictionPrice, predictionAccuracy, d, m, y };
}

let coinSymbol = [];

export default function WganPricePrediction({ model }) {
    const [rows, setRows] = React.useState([]);
    const [iconURL, setIconURL] = React.useState({});
    const [loading, setLoading] = React.useState(true);

    useEffect(() => {
        fetchCoinChannelData();
    }, []);

    const [isMobile, setIsMobile] = React.useState(false);
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        // Add event listener to handle window resize
        window.addEventListener('resize', handleResize);

        // Initial check for mobile
        setIsMobile(window.innerWidth <= 768);

        // Clean up event listener
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    //End  

    const fetchCoinChannelData = async () => {
        try {
            const data = await pricePrediction(model);
            let formattedData = Object.entries(data).map(([key, value], index) => {
                return createData(
                    index,
                    value["coin"],
                    value["day365"]["predicted_price"],
                    value["day365"]["r2_score"],
                    value['day1']['percentage_change_day'],
                    value['day30']['percentage_change_day'],
                    value['day365']['percentage_change_day'],

                );
            });
            pushCoins(formattedData);
            getResult();
            setRows(formattedData);
        } catch (error) {
            console.log("error ocuured", error);
        } finally {
            setLoading(false);
        }
    };
    const getResult = async () => {
        var result = await getCryptoLogoMap(coinSymbol);
        setIconURL(result);
        return result;
    };

    const pushCoins = (dataArray) => {
        dataArray.forEach((data) => {
            coinSymbol.push(data.coin);
        });
    };


    return (
        <>
            <Box position="relative" maxWidth="900px" margin="20px auto">


                <ToolTip arrow title={"At Auro Digital we have a deep expertise in AI and leverage various models, including LSTM, Transformer, GAN, and WGAN models to predict future prices of various cryptocurrencies that we share with our clients as ideational tools."} placement="top" enterTouchDelay={0}>

                    <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "center", marginTop: "10px", marginBottom: "30px" }}>
                        <Typography sx={{ fontWeight: "bold", textAlign: "center" }} variant={isMobile ? "h5" : "h4"}>
                            {model.toUpperCase()}{" Price Prediction"}
                        </Typography>
                        <div style={{ marginLeft: "5px" }}>
                            {
                                isMobile ?
                                    (
                                        <QuestionMarkSvg />
                                    ) : (
                                        <div />
                                    )}
                        </div>
                    </Box>
                </ToolTip>

                {loading ? (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "50px",
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                    >
                        <CircularProgress />
                    </div>
                ) : (
                    <div>

                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                                <TableHead
                                    style={{
                                        backgroundColor: "black",
                                        color: "white",
                                    }}
                                >
                                    <TableRow>
                                        <StyledTableCell>#</StyledTableCell>
                                        <StyledTableCell>Coin</StyledTableCell>

                                        <StyledTableCell align="center">Prediction Price (1Y)</StyledTableCell>
                                        <StyledTableCell align="center">Prediction Accuracy (1Y)</StyledTableCell>

                                        <StyledTableCell align="center" colSpan={3}>
                                            <div>(Predicted Price) </div>
                                            <div
                                                style={{
                                                    color: "white",
                                                    display: "flex",
                                                    gap: "20px",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <span>1d</span>
                                                <span>1m</span>
                                                <span>1y</span>
                                            </div>
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <StyledTableRow

                                            key={row.name}>
                                            <StyledTableCell component="th" scope="row">
                                                {row.index + 1}
                                            </StyledTableCell>
                                            <StyledTableCell component="th" scope="row">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Box>
                                                        <Icon name={row.coin?.toLowerCase()} url={iconURL[row.coin.toUpperCase()]} />
                                                    </Box>
                                                    <Box>
                                                        <Typography fontWeight="600" fontSize={12}>
                                                            {row.coin}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </StyledTableCell>

                                            <StyledTableCell align="center">{<Typography> {row.predictionPrice}</Typography>}</StyledTableCell>
                                            <StyledTableCell align="center">{<Typography> {row.predictionAccuracy}% </Typography>}</StyledTableCell>
                                            <StyledTableCell align="center">{<MarketRate type="percentage" value={row.d} />}</StyledTableCell>
                                            <StyledTableCell align="center">{<MarketRate type="percentage" value={row.m} />}</StyledTableCell>

                                            <StyledTableCell align="center">{<MarketRate type="percentage" value={row.y} />}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                )}
            </Box>




        </>
    );
}
